/**
 * Customer logo upload control for the customer settings General section.
 *
 * Shows the customer's current logo (or a placeholder), and lets a user
 * upload/replace or clear it. The logo rides the customer viewset's
 * multipart endpoints rather than the JSON-only consolidated settings
 * PATCH, so this control commits immediately and asks the parent to
 * reload the settings snapshot afterward.
 */

import BusinessIcon from '@mui/icons-material/Business';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadIcon from '@mui/icons-material/Upload';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeleteCustomerLogoMutation, useUploadCustomerLogoMutation } from '@sophocles/features/entities/customers';
import useDeleteConfirm from '@sophocles/hooks/useDeleteConfirm';

// Backend caps the upload at 15 MB; sophocles validates a tighter 10 MB
// so the user gets an instant client-side rejection instead of a round
// trip. Keep these in sync if the backend limit changes.
const MAX_LOGO_BYTES = 10 * 1024 * 1024;

export default function CustomerLogoField({ customerId, logoUrl, disabled, onChanged }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [uploadLogo] = useUploadCustomerLogoMutation();
  const [deleteLogo] = useDeleteCustomerLogoMutation();
  const { confirmDelete, dialogElement: deleteConfirmDialog } = useDeleteConfirm();

  const hasLogo = Boolean(logoUrl);

  const handlePick = () => {
    setError(null);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async event => {
    const file = event.target.files?.[0];
    // Reset the input so picking the same file again re-fires onChange.
    event.target.value = '';
    if (!file) return;

    if (file.size > MAX_LOGO_BYTES) {
      setError(t('customerSettings.v2.logoTooLarge', 'Image must be no more than 10 MB in size.'));
      return;
    }

    setBusy(true);
    setError(null);
    const result = await uploadLogo({ id: customerId, file });
    setBusy(false);
    if (result.error) {
      setError(result.error?.data?.logo?.[0] || t('customerSettings.v2.logoUploadFailed', 'Failed to upload logo.'));
    } else {
      onChanged?.();
    }
  };

  const handleClear = async () => {
    setBusy(true);
    setError(null);
    const result = await deleteLogo({ id: customerId });
    setBusy(false);
    if (result.error) {
      setError(t('customerSettings.v2.logoClearFailed', 'Failed to remove logo.'));
    } else {
      onChanged?.();
    }
  };

  const handleClearClick = () => {
    confirmDelete(customerId, { entityLabel: t('general.logo'), onConfirm: handleClear });
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={2} alignItems="center">
        {/*
          Logos are often wide/landscape — preview at natural aspect ratio
          (height-capped, width auto, contain) inside a fixed-height box so
          a square frame doesn't crop or distort the brand mark.
        */}
        <Box
          sx={{
            height: 64,
            width: 160,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
            color: 'text.secondary',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt=""
              sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <BusinessIcon />
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={busy ? <CircularProgress size={16} /> : <UploadIcon />}
            onClick={handlePick}
            disabled={disabled || busy}
          >
            {hasLogo ? t('customerSettings.v2.replaceLogo', 'Replace') : t('customerSettings.v2.uploadLogo', 'Upload')}
          </Button>
          {hasLogo ? (
            <Button
              variant="text"
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleClearClick}
              disabled={disabled || busy}
            >
              {t('actions.clear', 'Clear')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
      {error ? (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      <Box
        component="input"
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelected}
        sx={{ display: 'none' }}
      />
      {deleteConfirmDialog}
    </Stack>
  );
}

CustomerLogoField.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  logoUrl: PropTypes.string,
  disabled: PropTypes.bool,
  onChanged: PropTypes.func,
};

CustomerLogoField.defaultProps = {
  logoUrl: null,
  disabled: false,
  onChanged: undefined,
};
